import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
} from 'react';

import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import {colors} from '@pb/kit-stylus/config.json';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './label-pill.module.styl';

import type {
    ComponentProps,
    ForwardedRef,
    ReactNode,
} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

type NativeButtonProps = Omit<ComponentProps<'button'>, 'prefix' | 'name'>;

type refType = HTMLDivElement | HTMLButtonElement;

export interface LabelPillProps extends NativeButtonProps {
    /**
     * CSS color value applied as the pill's `background-color`. Pass any valid
     * CSS color string (hex, rgb, design-system token). Defaults to the gray
     * primary color.
     */
    color?: string,
    /**
     * Unique identifier for this pill. Used as the HTML `id` attribute of the
     * rendered root element.
     */
    id: string,
    /**
     * Primary text content of the pill. May be any React node — typically a
     * string but can include formatted text or icons.
     */
    name: ReactNode,
    /**
     * Called when the user clicks the close icon or presses `Backspace` /
     * `Delete`. Use to remove the pill from the owning list's state.
     */
    onRemove?: () => void,
    /**
     * Node rendered before `name` (e.g. a color swatch or category icon).
     * Wrapped in a `<span>` with prefix spacing styles.
     */
    prefix?: ReactNode,
    /**
     * Visual size of the pill. `"SMALL"` (default) truncates the name with an
     * ellipsis; `"MEDIUM"` allows the name to wrap to multiple lines.
     */
    size?: 'SMALL' | 'MEDIUM',
    /**
     * CSS color value applied as the pill's `color` (text color). Defaults to
     * the dark-blue primary color.
     */
    textColor?: string,
}

/**
 * Colored label chip with an optional remove affordance. Unlike `<Pill>`,
 * `LabelPill` accepts explicit `color` and `textColor` props for fully custom
 * branding (e.g. investor tag colors). Renders as a `<button>` when interactive
 * (`onClick` or `onRemove` present), otherwise as a `<div>`.
 *
 * ## Accessibility
 * - `Backspace` and `Delete` keys trigger `onRemove` when the pill is focused.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-strip-of-pills-pill-label-pill--documentation}
 */

export const LabelPill = forwardRef<refType, LabelPillProps>(
    function LabelPill(props: LabelPillProps, ref: ForwardedRef<refType>) {
        const {
            prefix,
            id,
            name,
            className,
            color = colors.primaryColors.gray,
            textColor = colors.primaryColors.darkBlueDark,
            size = 'SMALL',
            onRemove,
            onClick,
            ...restProps
        } = props;

        const divRef = useRef<HTMLDivElement | null>(null);
        const buttonRef = useRef<HTMLButtonElement | null>(null);

        const interactive = onClick || onRemove;

        const actualRef = interactive ? buttonRef : divRef;

        useImperativeHandle(ref, () => actualRef.current as refType);

        const handleRemove = (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLSpanElement>) => {
            if (onRemove) {
                event.stopPropagation();
                onRemove();
            }
        };

        const handleOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (onClick) {
                onClick(event);
            }
        };

        const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
                handleRemove(event);
            }
        };

        const labelPillClassName = classNames(
            'label-pill',
            className,
            {'label-pill-small': size === 'SMALL'},
            {'label-pill-medium': size === 'MEDIUM'},
        );

        const labelPillProps = {
            ...restProps,
            className: labelPillClassName,
            style: {
                color: textColor,
                backgroundColor: color,
            },
            onKeyUp: handleKeyUp,
            onClick: handleOnClick,
        };

        const content = (
            <>
                {prefix ? <span className={classes['label-pill__prefix']}>{prefix}</span> : null}
                <span className={classNames('label-pill__name', {ellipsis: size !== 'MEDIUM'})}>
                    {name}
                </span>
                {onRemove ? (
                    <span
                        className={classes['label-pill__button']}
                        onClick={handleRemove}
                        aria-hidden="true"
                        role="button">
                        <Icon type={Icons.CLOSE} size={Sizes.Size_10}/>
                    </span>
                ) : null}
            </>
        );

        return interactive ? (
            <button ref={buttonRef} {...labelPillProps}>
                {content}
            </button>
        ) : (
            // @ts-expect-error
            <div ref={divRef} {...labelPillProps}>
                {content}
            </div>
        );

    },
);

LabelPill.displayName = 'LabelPill';
